/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import {
  IconDownload,
  IconPlus,
  IconSearch,
  IconX,
  IconTruck,
  IconArrowBack,
  IconPackage,
  IconRoute,
  IconLoader2,
  IconEye,
  IconClipboardCheck,
  IconRotateClockwise,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import * as XLSX from "xlsx";
import ListView from "../../../components/common/ListView";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import NoDataFound from "../../../components/common/NoDataFound";
import Pagination from "../../../components/common/Pagination";
import EntriesSelector from "../../../components/common/EntriesSelector";
import ColumnsFilter from "../../../components/common/ColumnsFilter";
import NewSelectInput from "../../../components/NewSelectInput";
import useDebounce from "../../../utilities/useDebounce";
import { useSelector } from "react-redux";
import { usePermission } from "../../../hooks/usePermissionTabs";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import formatDate, { formatDateTime, formatTime } from "../../../utilities/helper";
import { fetch3 } from "../../../commonApis/commonApis";
import { useQueryClient } from "@tanstack/react-query";
import { T, useLanguage } from "../../../context/LanguageContext";

// API Hooks
import { useFetchUnifiedManifestData } from "../../../apis/unifiedManifestApis";
import {
  usePickRequestMutation,
  useLinehualManifestGenerateMutation,
  useAssignDriverMutation,
  useAssignReceiverMutation,
  usePickRequestInboundMutation,
  usePickCollectedMutation,
  useAssignAssignlinehaulVehicleMutation,
  useAssignReceiverLinehaulManifestMutation,
  useLineHaulRequestInboundMutation,
} from "../../../apis/pickupApis";
import {
  useReturnToShipperMutation,
  useAssignDriverMutation as useAssignReturnDriverMutation,
  useReturnToShipperDocMutation,
} from "../../../apis/returnToShipperApi";
import { useProductManifestCreateMutation } from "../../../apis/productManifestApis";

// Modals
import PickupViewModal from "./PickupViewModal";
import LineHaulViewModal from "./LineHaulViewModal";
import ViewDetailsReturnToShipperModal from "./ViewDetailsReturnToShipperModal";
import ViewProductRequestModal from "./ViewProductRequestModal";

import GeneratePickupModal from "./GeneratePickupModal";
import LinehaulRequestModal from "./LinehaulRequestModal";
import ReturnToShipperModal from "./ReturnToShipperModal";

import AssignDriver from "./AssignDriver";
import RiwVerificationModal from "./RiwVerificationModal";
import AssignReceiverModal from "./AssignReceiverModal";
import PickupCollectModal from "./PickupCollectModal";
import AssignLineHoulVehicleModal from "./AssignLineHoulVehicleModal";
import AssignReceiverLineHaulManifestModal from "./AssignReceiverLineHaulManifestModal";
import UploadModal from "./UploadModal";
import AssignCheckStaff from "./AssignCheckStaff";
import AssignInventoryStaff from "./AssignInventoryStaff";
import AddProductInventoryModal from "./AddProductInventoryModal";

import { useMerchantAllListData } from "../../../apis/merchantApis";
import { useProductListDataById } from "../../../apis/promoApis";
import MultiSelectInput from "../../../components/common/MultiSelectInput";

const url = process.env.REACT_APP_BASE_URL;

type ManifestType = "pickup" | "linehaul" | "return" | "product" | "";

const TYPE_ICONS: Record<string, React.ReactNode> = {
  pickup: <IconTruck size={14} />,
  linehaul: <IconRoute size={14} />,
  return: <IconArrowBack size={14} />,
  product: <IconPackage size={14} />,
};

const TYPE_COLORS: Record<string, string> = {
  pickup: "bg-blue-50 text-blue-700 border-blue-200",
  linehaul: "bg-purple-50 text-purple-700 border-purple-200",
  return: "bg-orange-50 text-orange-700 border-orange-200",
  product: "bg-green-50 text-green-700 border-green-200",
};

const STATUS_COLORS: Record<string, string> = {
  GENERATED: "bg-amber-50 border border-amber-300 text-amber-800",
  ASSIGNED: "bg-blue-50 border border-blue-300 text-blue-800",
  ASSIGNED_TO_DRIVER: "bg-blue-50 border border-blue-300 text-blue-800",
  ASSIGNED_TO_STAFF: "bg-indigo-50 border border-indigo-300 text-indigo-800",
  COLLECTED: "bg-slate-50 border border-slate-300 text-slate-700",
  COMPLETED: "bg-green-50 border border-green-300 text-green-800",
  PENDING: "bg-red-50 border border-red-300 text-red-800",
  RECEIVE_INBOUND: "bg-slate-50 border border-slate-300 text-slate-700",
  MANIFEST_REQUEST: "bg-blue-50 border border-blue-300 text-blue-800",
  VERIFIED: "bg-green-50 border border-green-300 text-green-800",
  IN_PROCESS: "bg-sky-50 border border-sky-300 text-sky-800",
  ON_PROCESS: "bg-sky-50 border border-sky-300 text-sky-800",
  UPDATED: "bg-purple-50 border border-purple-300 text-purple-800",
  ASSIGN_TO_INVENTORY_STAFF:
    "bg-indigo-50 border border-indigo-300 text-indigo-800",
  ADD_INVENTORY: "bg-teal-50 border border-teal-300 text-teal-800",
  ON_HOLD: "bg-green-50 border border-green-300 text-green-800",
  CLOSED: "bg-rose-50 border border-rose-300 text-rose-800",
  CHECKED: "bg-emerald-50 border border-emerald-300 text-emerald-800",
};

const fmtStatus = (s: string) => {
  if (s === "ON_HOLD") return "Verified";
  if (s === "ON_PROCESS") return "In Process";
  return s
    ? s
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : "";
};

const SectionHeader = ({
  icon: Icon,
  title,
  className = "border-b border-[#FBD037]/30 pb-2 mb-3",
}: {
  icon: any;
  title: string;
  className?: string;
}) => (
  <div className={`flex items-center gap-1.5 ${className}`}>
    <div className="p-1 rounded-lg bg-[#FBD037]/20">
      <Icon size={14} className="text-[#212121]" stroke={1.5} />
    </div>
    <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
      {title}
    </p>
  </div>
);

interface Props {
  setHeaderButtons?: (n: React.ReactNode) => void;
}

export default function UnifiedManifestTab({ setHeaderButtons }: Props = {}) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchFor, setSearchFor] = useState("");
  const expandedIdsRef = useRef<Set<string>>(new Set());
  const [, forceUpdate] = useState(0);

  const toggleRow = (id: string) => {
    if (expandedIdsRef.current.has(id)) {
      expandedIdsRef.current.delete(id);
    } else {
      expandedIdsRef.current.add(id);
    }
    forceUpdate((n) => n + 1);
  };
  const [showSearch, setShowSearch] = useState(false);
  const [typeFilter, setTypeFilter] = useState<ManifestType>("");
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [openDropdown, setOpenDropdown] = useState<any>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debouncedSearch = useDebounce(searchFor, 500);

  // View States
  const [selectedPickup, setSelectedPickup] = useState<any>(null);
  const [isPickupViewOpen, setIsPickupViewOpen] = useState(false);
  const [selectedPickupAwbIds, setSelectedPickupAwbIds] = useState<string[]>(
    [],
  );
  const [selectedAwbActions, setSelectedAwbActions] = useState<{
    type: "assign" | "collect" | "inbound" | "assign-staff";
    ids: string[];
  } | null>(null);
  const [isLinehaulViewOpen, setIsLinehaulViewOpen] = useState(false);
  const [isReturnViewOpen, setIsReturnViewOpen] = useState(false);
  const [returnToShipperDetails, setReturnToShipperDetails] =
    useState<any>(null);
  const [isProductViewOpen, setIsProductViewOpen] = useState(false);

  // Create Form States
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createType, setCreateType] = useState<ManifestType>("");
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);

  // Action Modals State
  const [openAssignDriver, setOpenAssignDriver] = useState(false);
  const [openRiwVerification, setOpenRiwVerification] = useState(false);
  const [openAssignReceiver, setOpenAssignReceiver] = useState(false);
  const [openPickupCollect, setOpenPickupCollect] = useState(false);

  const [openAssignLinehaulVehicle, setOpenAssignLinehaulVehicle] =
    useState(false);
  const [openAssignReceiverLinehaul, setOpenAssignReceiverLinehaul] =
    useState(false);

  const [openUploadDoc, setOpenUploadDoc] = useState(false);

  const [openAssignCheckStaff, setOpenAssignCheckStaff] = useState(false);
  const [openAssignInventoryStaff, setOpenAssignInventoryStaff] =
    useState(false);
  const [openAddProductInventory, setOpenAddProductInventory] = useState(false);

  // For product update mode
  const [productViewType, setProductViewType] = useState<"view" | "update">(
    "view",
  );

  // Local state for uploaded file
  const [doc, setDoc] = useState<any>(null);

  // Local state for RiwVerification modal
  const [missingToggle, setMissingToggle] = useState(false);
  const [damageToggle, setDamageToggle] = useState(false);
  const [missingMessage, setMissingMessage] = useState("");
  const [damageMessage, setDamageMessage] = useState("");
  const [awbNumbers, setAwbNumbers] = useState<string[]>([]);

  // Mutations
  const { mutate: genratePickRequest, isPending: genratePickRequestPending } =
    usePickRequestMutation();
  const { mutate: sendCompanyRequest, isPending: sendPending } =
    useLinehualManifestGenerateMutation();
  const {
    mutate: genrateReturnToShipper,
    isPending: genrateReturnToShipperPending,
  } = useReturnToShipperMutation();
  const { mutate: createPromo, isPending: creatingProduct } =
    useProductManifestCreateMutation();

  // Action Mutations
  const { mutate: assignPickupDriver, isPending: isAssignPickupDriverPending } =
    useAssignDriverMutation();
  const {
    mutate: assignPickupReceiver,
    isPending: isAssignPickupReceiverPending,
  } = useAssignReceiverMutation();
  const { mutate: pickRequestInbound, isPending: isPickRequestInboundPending } =
    usePickRequestInboundMutation();
  const { mutate: pickCollected, isPending: isPickCollectedPending } =
    usePickCollectedMutation();

  const {
    mutate: assignLinehaulVehicle,
    isPending: isAssignLinehaulVehiclePending,
  } = useAssignAssignlinehaulVehicleMutation();
  const {
    mutate: assignLinehaulReceiver,
    isPending: isAssignLinehaulReceiverPending,
  } = useAssignReceiverLinehaulManifestMutation();
  const {
    mutate: linehaulRequestInbound,
    isPending: isLinehaulRequestInboundPending,
  } = useLineHaulRequestInboundMutation();

  const { mutate: assignReturnDriver, isPending: isAssignReturnDriverPending } =
    useAssignReturnDriverMutation();
  const { mutate: uploadReturnDoc, isPending: isUploadReturnDocPending } =
    useReturnToShipperDocMutation();

  // Forms
  const {
    register: pickupRegister,
    setValue: pickupSetValue,
    watch: pickupWatch,
    control: pickupControl,
    clearErrors: pickupClearErrors,
    handleSubmit: handlePickupSubmit,
    reset: pickupReset,
    formState: { errors: pickupErrors },
    trigger: pickupTrigger,
  } = useForm();

  const {
    register: linehaulRegister,
    setValue: linehaulSetValue,
    watch: linehaulWatch,
    clearErrors: linehaulClearErrors,
    handleSubmit: handleLinehaulSubmit,
    reset: linehaulReset,
    formState: { errors: linehaulErrors },
    trigger: linehaulTrigger,
  } = useForm();

  const {
    register: returnRegister,
    setValue: returnSetValue,
    watch: returnWatch,
    control: returnControl,
    clearErrors: returnClearErrors,
    handleSubmit: handleReturnSubmit,
    reset: returnReset,
    formState: { errors: returnErrors },
  } = useForm();

  const {
    register: productRegister,
    setValue: productSetValue,
    watch: productWatch,
    clearErrors: productClearErrors,
    handleSubmit: handleProductSubmit,
    reset: productReset,
    formState: { errors: productErrors },
  } = useForm();

  const [linehaulStep, setLinehaulStep] = useState(1);

  useEffect(() => {
    setLinehaulStep(1);
  }, [createType, isCreateOpen]);

  // Additional forms for modals
  const {
    register: assignDriverRegister,
    setValue: assignDriverSetValue,
    watch: assignDriverWatch,
    control: assignDriverControl,
    clearErrors: assignDriverClearErrors,
    handleSubmit: handleAssignDriverSubmit,
    reset: assignDriverReset,
    formState: { errors: assignDriverErrors },
  } = useForm({
    defaultValues: {
      pickupId: "",
      driverId: "",
      driverContact: "",
    },
  });

  const {
    register: assignReceiverRegister,
    setValue: assignReceiverSetValue,
    watch: assignReceiverWatch,
    control: assignReceiverControl,
    clearErrors: assignReceiverClearErrors,
    handleSubmit: handleAssignReceiverSubmitForm,
    reset: assignReceiverReset,
    formState: { errors: assignReceiverErrors },
  } = useForm({
    defaultValues: {
      staffId: "",
    },
  });

  const {
    register: assignLinehaulRegister,
    setValue: assignLinehaulSetValue,
    watch: assignLinehaulWatch,
    control: assignLinehaulControl,
    clearErrors: assignLinehaulClearErrors,
    handleSubmit: handleAssignLinehaulSubmitForm,
    reset: assignLinehaulReset,
    formState: { errors: assignLinehaulErrors },
  } = useForm({
    defaultValues: {
      assignType: "LINEHAUL",
      linehaulCompanyId: "",
      driverId: "",
      driverContact: "",
      driverIqamaNumber: "",
      driverNameLine: "",
    },
  });

  const {
    register: pickupCollectRegister,
    setValue: pickupCollectSetValue,
    watch: pickupCollectWatch,
    clearErrors: pickupCollectClearErrors,
    handleSubmit: handlePickupCollectSubmitForm,
    reset: pickupCollectReset,
    formState: { errors: pickupCollectErrors },
  } = useForm();

  const { data: clients } = useMerchantAllListData();
  const productClientId = productWatch("toClient");
  const { data: products } = useProductListDataById(productClientId);

  const { refetch, isLoading, isFetching } = useFetchUnifiedManifestData(
    pageNo,
    limit,
    debouncedSearch,
    typeFilter || undefined,
  );
  const data = useSelector((state: any) => state.unifiedManifest.list);
  const manifests: any[] = data?.manifests ?? [];

  useEffect(() => {
    if (selectedPickup && manifests.length > 0) {
      let updated = manifests.find((m) => m.id === selectedPickup.id);
      if (!updated) {
        for (const manifest of manifests) {
          if (manifest.children && manifest.children.length > 0) {
            const child = manifest.children.find(
              (c: any) => c.id === selectedPickup.id,
            );
            if (child) {
              updated = child;
              break;
            }
          }
        }
      }
      if (updated) {
        setSelectedPickup(updated);
      }
    }
  }, [manifests, selectedPickup?.id]);

  useEffect(() => {
    refetch();
  }, [pageNo, limit, debouncedSearch, typeFilter]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      )
        setOpenDropdown(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Reset createType when create panel closes
  useEffect(() => {
    if (!isCreateOpen) {
      setCreateType("");
    }
  }, [isCreateOpen]);

  // Render stats capsule in the top-right page header
  useEffect(() => {
    if (!setHeaderButtons) return;
    setHeaderButtons(
      <div className="flex items-center gap-2 bg-white/70 backdrop-blur-sm px-4 py-1.5 rounded-xl border border-slate-200 shadow-sm text-[11px] select-none font-bold min-h-8">
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">{t("Total")}:</span>
          <span className="text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded">
            {data?.stats?.total ?? 0}
          </span>
        </div>
        <span className="h-3 w-px bg-slate-200" />
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">{t("Draft")}:</span>
          <span className="text-rose-700 bg-rose-50 border border-rose-100 px-1.5 py-0.5 rounded">
            {data?.stats?.draft ?? 0}
          </span>
        </div>
        <span className="h-3 w-px bg-slate-200" />
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">{t("Active")}:</span>
          <span className="text-indigo-700 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded">
            {data?.stats?.active ?? 0}
          </span>
        </div>
        <span className="h-3 w-px bg-slate-200" />
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">{t("Verified")}:</span>
          <span className="text-green-700 bg-green-50 border border-green-100 px-1.5 py-0.5 rounded">
            {data?.stats?.verified ?? 0}
          </span>
        </div>
      </div>,
    );
    return () => setHeaderButtons?.(null);
  }, [
    setHeaderButtons,
    data?.stats?.total,
    data?.stats?.draft,
    data?.stats?.active,
    data?.stats?.verified,
  ]);

  const fetchReturnToShipperDetails = async (id: string) => {
    try {
      const res = await fetch3(
        `${url}/api/return-to-shipper/detailsById/${id}`,
        "get",
      );
      if (res && res.data) {
        setReturnToShipperDetails(res.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Submit Handlers for Actions
  const handleAssignDriver = (data: any) => {
    assignPickupDriver(
      {
        ...data,
        shipmentIds:
          selectedPickupAwbIds.length > 0 ? selectedPickupAwbIds : undefined,
      },
      {
        onSuccess: () => {
          setOpenAssignDriver(false);
          assignDriverReset();
          setSelectedPickupAwbIds([]);
          setSelectedRows([]);
          refetch();
          queryClient.invalidateQueries({ queryKey: ["pickupLocationData"] });
          toast.success("Assign Driver Successfully.");
        },
        onError: (error: any) => {
          toast.error(
            `${error?.message || error || "Failed to assign driver"}`,
          );
        },
      },
    );
  };

  const handleAssignReceiver = (data: any) => {
    assignPickupReceiver(
      {
        ...data,
        pickupId: selectedPickup?.id,
        shipmentIds:
          selectedPickupAwbIds.length > 0 ? selectedPickupAwbIds : undefined,
      },
      {
        onSuccess: () => {
          setOpenAssignReceiver(false);
          assignReceiverReset();
          setSelectedPickupAwbIds([]);
          setSelectedRows([]);
          refetch();
          queryClient.invalidateQueries({ queryKey: ["pickupLocationData"] });
          toast.success("Assign Receiver Successfully.");
        },
        onError: (error: any) => {
          toast.error(
            `${error?.message || error || "Failed to assign receiver"}`,
          );
        },
      },
    );
  };

  const handlePickupInbound = () => {
    pickRequestInbound(
      {
        verifiedShipments: !missingToggle && !damageToggle ? selectedRows : [],
        pickupId: selectedPickup?.id,
        missingShipments: missingToggle ? selectedRows : [],
        damageShipments: damageToggle ? selectedRows : [],
        missingMessage: missingToggle && missingMessage ? missingMessage : "",
        damageMessage: damageToggle && damageMessage ? damageMessage : "",
      },
      {
        onSuccess: () => {
          setOpenRiwVerification(false);
          setSelectedRows([]);
          setSelectedPickupAwbIds([]);
          setMissingToggle(false);
          setDamageToggle(false);
          setMissingMessage("");
          setDamageMessage("");
          setAwbNumbers([]);
          refetch();
          queryClient.invalidateQueries({ queryKey: ["pickupLocationData"] });
          toast.success("Inbound Successfully.");
        },
        onError: (error: any) => {
          toast.error(`${error?.message || error || "Inbound failed"}`);
        },
      },
    );
  };

  const handlePickupCollected = () => {
    pickCollected(
      {
        pickupId: selectedPickup?.id,
        collectedShipments: selectedRows || [],
      },
      {
        onSuccess: () => {
          setOpenPickupCollect(false);
          setSelectedRows([]);
          setSelectedPickupAwbIds([]);
          pickupCollectReset();
          refetch();
          queryClient.invalidateQueries({ queryKey: ["pickupLocationData"] });
          toast.success("Pickup Collected Successfully.");
        },
        onError: (error: any) => {
          toast.error(
            `${error?.message || error || "Pickup Collection failed"}`,
          );
        },
      },
    );
  };

  const handleAssignLinehaulVehicleSubmit = (formValues: any) => {
    const payload: any = {
      id: selectedPickup?.id,
      assignType: formValues.assignType,
    };
    if (payload?.assignType === "LINEHAUL") {
      payload.linehaulCompanyId = formValues?.linehaulCompanyId;
    } else {
      payload.driverId = formValues?.driverId;
    }
    assignLinehaulVehicle(payload, {
      onSuccess: () => {
        toast.success("Linehaul vehicle assigned successfully ✅");
        setOpenAssignLinehaulVehicle(false);
        assignLinehaulReset();
        setSelectedRows([]);
        refetch();
        queryClient.invalidateQueries({ queryKey: ["linehaulShipments"] });
      },
      onError: (error: any) => {
        toast.error(error?.message || "Failed to assign driver/linehaul");
      },
    });
  };

  const handleAssignLinehaulReceiverSubmit = (data: any) => {
    assignLinehaulReceiver(
      { ...data, id: selectedPickup?.id },
      {
        onSuccess: () => {
          setOpenAssignReceiverLinehaul(false);
          assignReceiverReset();
          setSelectedRows([]);
          refetch();
          queryClient.invalidateQueries({ queryKey: ["linehaulShipments"] });
          toast.success("Assign Receiver Successfully.");
        },
        onError: (error: any) => {
          toast.error(
            `${error?.message || error || "Failed to assign receiver"}`,
          );
        },
      },
    );
  };

  const handleLinehaulInbound = () => {
    linehaulRequestInbound(
      {
        id: selectedPickup?.id,
        verifiedShipments: !missingToggle && !damageToggle ? selectedRows : [],
        missingShipments: missingToggle ? selectedRows : [],
        damageShipments: damageToggle ? selectedRows : [],
        missingMessage: missingToggle && missingMessage ? missingMessage : "",
        damageMessage: damageToggle && damageMessage ? damageMessage : "",
      },
      {
        onSuccess: () => {
          setOpenRiwVerification(false);
          setSelectedRows([]);
          setMissingToggle(false);
          setDamageToggle(false);
          setMissingMessage("");
          setDamageMessage("");
          setAwbNumbers([]);
          refetch();
          queryClient.invalidateQueries({ queryKey: ["linehaulShipments"] });
          toast.success("Inbound Successfully.");
        },
        onError: (error: any) => {
          toast.error(`${error?.message || error || "Inbound failed"}`);
        },
      },
    );
  };

  const handleAssignReturnDriverSubmit = (data: any) => {
    assignReturnDriver(
      {
        returnId: selectedPickup?.id,
        driverId: data.driverId,
      },
      {
        onSuccess: () => {
          setOpenAssignDriver(false);
          assignDriverReset();
          setSelectedRows([]);
          refetch();
          if (selectedPickup?.id) {
            fetchReturnToShipperDetails(selectedPickup.id);
          }
          toast.success("Assign Successfully.");
        },
        onError: (error: any) => {
          toast.error(
            `${error?.message || error || "Failed to assign driver"}`,
          );
        },
      },
    );
  };

  const handleUploadReturnDoc = () => {
    if (!doc) {
      toast.error("Please select a document");
      return;
    }
    uploadReturnDoc(
      { image: doc, id: selectedPickup?.id },
      {
        onSuccess: () => {
          setOpenUploadDoc(false);
          setDoc(null);
          refetch();
          if (selectedPickup?.id) {
            fetchReturnToShipperDetails(selectedPickup.id);
          }
          toast.success("Upload Successfully.");
        },
        onError: (error: any) => {
          toast.error(`${error?.message || error || "Upload failed"}`);
        },
      },
    );
  };

  // ── Permissions ──────────────────────────────────────────────────────
  const { allowed: canCreatePickup } = usePermission("pickup_manifests", "create");
  const { allowed: canCreateLinehaul } = usePermission("linehaul_manifests", "create");
  const { allowed: canCreateReturn } = usePermission("return_to_shipper", "create");
  const { allowed: canCreateProduct } = usePermission("product_manifest", "create");
  const { allowed: canUpdatePickup } = usePermission("pickup_manifests", "update");
  const { allowed: canUpdateLinehaul } = usePermission("linehaul_manifests", "update");
  const { allowed: canUpdateReturn } = usePermission("return_to_shipper", "update");
  const { allowed: canUpdateProduct } = usePermission("product_manifest", "update");
  const { allowed: canReadPickup } = usePermission("pickup_manifests", "read");
  const { allowed: canReadLinehaul } = usePermission("linehaul_manifests", "read");
  const { allowed: canReadReturn } = usePermission("return_to_shipper", "read");
  const { allowed: canReadProduct } = usePermission("product_manifest", "read");

  const canCreateAnyManifest =
    canCreatePickup || canCreateLinehaul || canCreateReturn || canCreateProduct;

  // Filter dropdown options – only show types the user can read
  const filterOptions = [
    ...(canReadPickup ? [{ id: "pickup", value: "pickup", text: "Shipment" }] : []),
    ...(canReadLinehaul ? [{ id: "linehaul", value: "linehaul", text: "Linehaul" }] : []),
    ...(canReadReturn ? [{ id: "return", value: "return", text: "Return to Shipper" }] : []),
    ...(canReadProduct ? [{ id: "product", value: "product", text: "Product" }] : []),
  ];

  // Create-type dropdown options – only show types the user can create
  const createOptions = [
    ...(canCreatePickup ? [{ id: "pickup", value: "pickup", text: "Shipment" }] : []),
    ...(canCreateLinehaul ? [{ id: "linehaul", value: "linehaul", text: "Linehaul" }] : []),
    ...(canCreateReturn ? [{ id: "return", value: "return", text: "Return to Shipper" }] : []),
    ...(canCreateProduct ? [{ id: "product", value: "product", text: "Product" }] : []),
  ];

  const renderViewHeaderActions = (row: any) => {
    if (!row) return null;
    const actions: React.ReactNode[] = [];

    if (row.type === "pickup" && canUpdatePickup) {
      if (row.status === "GENERATED") {
        actions.push(
          <button
            key="assign-driver"
            onClick={() => {
              assignDriverReset();
              setOpenAssignDriver(true);
            }}
            className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors shadow-sm hover:shadow active:scale-[0.98]"
          >
            {t("Assign Driver")}
          </button>,
        );
      }
      if (row.status === "ASSIGNED_TO_DRIVER") {
        actions.push(
          <button
            key="update-driver"
            onClick={() => {
              assignDriverReset();
              setOpenAssignDriver(true);
            }}
            className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors shadow-sm hover:shadow active:scale-[0.98]"
          >
            {t("Change Driver")}
          </button>,
          <button
            key="pickup-collect"
            onClick={() => {
              setSelectedRows([]);
              setOpenPickupCollect(true);
            }}
            className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors shadow-sm hover:shadow active:scale-[0.98]"
          >
            {t("Pickup Collect")}
          </button>,
        );
      }
      if (row.status === "COLLECTED") {
        actions.push(
          <button
            key="assign-receiver"
            onClick={() => {
              assignReceiverReset();
              setOpenAssignReceiver(true);
            }}
            className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors shadow-sm hover:shadow active:scale-[0.98]"
          >
            {t("Assign Receiver")}
          </button>,
        );
      }
      if (["PENDING", "COLLECTED", "ASSIGNED_TO_STAFF"].includes(row.status)) {
        actions.push(
          <button
            key="inbound"
            onClick={() => {
              setSelectedRows([]);
              setMissingToggle(false);
              setDamageToggle(false);
              setMissingMessage("");
              setDamageMessage("");
              setAwbNumbers([]);
              setOpenRiwVerification(true);
            }}
            className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors shadow-sm hover:shadow active:scale-[0.98]"
          >
            {t("Inbound")}
          </button>,
        );
      }
    }

    if (row.type === "linehaul" && canUpdateLinehaul) {
      if (row.status === "GENERATED") {
        actions.push(
          <button
            key="assign-linehaul"
            onClick={() => {
              assignLinehaulReset();
              setOpenAssignLinehaulVehicle(true);
            }}
            className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors shadow-sm hover:shadow active:scale-[0.98]"
          >
            {t("Assign Vehicle")}
          </button>,
        );
      }
      if (row.status === "ASSIGNED") {
        actions.push(
          <button
            key="assign-receiver-lh"
            onClick={() => {
              assignReceiverReset();
              setOpenAssignReceiverLinehaul(true);
            }}
            className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors shadow-sm hover:shadow active:scale-[0.98]"
          >
            {t("Assign Receiver")}
          </button>,
        );
      }
      if (["PENDING", "COLLECTED", "ASSIGNED"].includes(row.status)) {
        actions.push(
          <button
            key="inbound-lh"
            onClick={() => {
              setSelectedRows([]);
              setMissingToggle(false);
              setDamageToggle(false);
              setMissingMessage("");
              setDamageMessage("");
              setAwbNumbers([]);
              setOpenRiwVerification(true);
            }}
            className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors shadow-sm hover:shadow active:scale-[0.98]"
          >
            {t("Inbound")}
          </button>,
        );
      }
    }

    if (row.type === "return" && canUpdateReturn) {
      if (row.status === "GENERATED") {
        actions.push(
          <button
            key="assign-driver-ret"
            onClick={() => {
              assignDriverReset();
              setOpenAssignDriver(true);
            }}
            className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors shadow-sm hover:shadow active:scale-[0.98]"
          >
            {t("Assign Driver")}
          </button>,
        );
      }
      const hasDoc = row.image || returnToShipperDetails?.image;
      if (!hasDoc) {
        actions.push(
          <button
            key="upload-doc-ret"
            onClick={() => {
              setDoc(null);
              setOpenUploadDoc(true);
            }}
            className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors shadow-sm hover:shadow active:scale-[0.98]"
          >
            {t("Upload Doc")}
          </button>,
        );
      }
    }

    if (row.type === "product" && canUpdateProduct) {
      if (
        row.status === "UPDATED" ||
        (row.createdByType === "CLIENT" && row.status === "MANIFEST_REQUEST")
      ) {
        actions.push(
          <button
            key="assign-staff-prod"
            onClick={() => {
              setOpenAssignCheckStaff(true);
            }}
            className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors shadow-sm hover:shadow active:scale-[0.98]"
          >
            {t("Assign To Staff")}
          </button>,
        );
      }
      if (
        row.status === "UPDATED" ||
        row.status === "ON_PROCESS" ||
        row.status === "ASSIGN_TO_STAFF" ||
        row.status === "ASSIGN_TO_INVENTORY_STAFF" ||
        row.status === "ON_HOLD" ||
        row.status === "VERIFIED" ||
        row.status === "ADD_INVENTORY" ||
        (row.status === "MANIFEST_REQUEST" && row.createdByType !== "ADMIN")
      ) {
        if (productViewType === "update") {
          actions.push(
            <button
              key="close-verify-prod"
              onClick={() => {
                setProductViewType("view");
              }}
              className="px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 border border-red-200 rounded-lg text-xs font-semibold transition-colors shadow-sm hover:shadow active:scale-[0.98]"
            >
              {t("Cancel")}
            </button>,
          );
        } else {
          const isAlreadyVerified =
            row.status === "VERIFIED" ||
            row.status === "ADD_INVENTORY" ||
            row.status === "COMPLETED";

          actions.push(
            <button
              key="check-verify-prod"
              onClick={() => {
                setProductViewType("update");
              }}
              className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors shadow-sm hover:shadow active:scale-[0.98]"
            >
              {isAlreadyVerified ? t("Edit Verify Qty") : t("Check & Verify")}
            </button>,
          );
        }
      }
      if (
        row.status === "VERIFIED" ||
        row.status === "ON_HOLD" ||
        row.status === "ON_PROCESS"
      ) {
        actions.push(
          <button
            key="assign-inv-staff-prod"
            onClick={() => {
              setOpenAssignInventoryStaff(true);
            }}
            className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors shadow-sm hover:shadow active:scale-[0.98]"
          >
            Assign Inv Staff
          </button>,
        );
      }
    }

    return actions;
  };

  const renderContextActions = (row: any) => {
    const actions: React.ReactNode[] = [];

    if (row.type === "pickup" && canUpdatePickup) {
      if (row.status === "GENERATED") {
        actions.push(
          <button
            key="assign-driver"
            onClick={() => {
              setOpenDropdown(null);
              setSelectedPickup(row);
              assignDriverReset();
              setOpenAssignDriver(true);
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <IconTruck size={14} />
            <span>{t("Assign Driver")}</span>
          </button>,
        );
      }
      if (row.status === "ASSIGNED_TO_DRIVER") {
        actions.push(
          <button
            key="update-driver"
            onClick={() => {
              setOpenDropdown(null);
              setSelectedPickup(row);
              assignDriverReset();
              setOpenAssignDriver(true);
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <IconTruck size={14} />
            <span>{t("Update Driver")}</span>
          </button>,
          <button
            key="pickup-collect"
            onClick={() => {
              setOpenDropdown(null);
              setSelectedPickup(row);
              setSelectedRows([]);
              setOpenPickupCollect(true);
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <IconClipboardCheck size={14} />
            <span>{t("Pickup Collect")}</span>
          </button>,
        );
      }
      if (row.status === "COLLECTED") {
        actions.push(
          <button
            key="assign-receiver"
            onClick={() => {
              setOpenDropdown(null);
              setSelectedPickup(row);
              assignReceiverReset();
              setOpenAssignReceiver(true);
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <IconPlus size={14} />
            <span>{t("Assign Receiver")}</span>
          </button>,
        );
      }
      if (["PENDING", "COLLECTED", "ASSIGNED_TO_STAFF"].includes(row.status)) {
        actions.push(
          <button
            key="inbound"
            onClick={() => {
              setOpenDropdown(null);
              setSelectedPickup(row);
              setSelectedRows([]);
              setMissingToggle(false);
              setDamageToggle(false);
              setMissingMessage("");
              setDamageMessage("");
              setAwbNumbers([]);
              setOpenRiwVerification(true);
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <IconPlus size={14} />
            <span>{t("Inbound")}</span>
          </button>,
        );
      }
    }

    if (row.type === "linehaul" && canUpdateLinehaul) {
      if (row.status === "GENERATED") {
        actions.push(
          <button
            key="assign-linehaul"
            onClick={() => {
              setOpenDropdown(null);
              setSelectedPickup(row);
              assignLinehaulReset();
              setOpenAssignLinehaulVehicle(true);
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <IconRoute size={14} />
            <span>{t("Assign Linehaul Vehicle")}</span>
          </button>,
        );
      }
      if (row.status === "ASSIGNED") {
        actions.push(
          <button
            key="assign-receiver-lh"
            onClick={() => {
              setOpenDropdown(null);
              setSelectedPickup(row);
              assignReceiverReset();
              setOpenAssignReceiverLinehaul(true);
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <IconPlus size={14} />
            <span>{t("Assign Receiver")}</span>
          </button>,
        );
      }
      if (["PENDING", "COLLECTED", "ASSIGNED"].includes(row.status)) {
        actions.push(
          <button
            key="inbound-lh"
            onClick={() => {
              setOpenDropdown(null);
              setSelectedPickup(row);
              setSelectedRows([]);
              setMissingToggle(false);
              setDamageToggle(false);
              setMissingMessage("");
              setDamageMessage("");
              setAwbNumbers([]);
              setOpenRiwVerification(true);
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <IconPlus size={14} />
            <span>{t("Inbound")}</span>
          </button>,
        );
      }
    }

    if (row.type === "return" && canUpdateReturn) {
      if (row.status === "GENERATED") {
        actions.push(
          <button
            key="assign-driver-ret"
            onClick={() => {
              setOpenDropdown(null);
              setSelectedPickup(row);
              assignDriverReset();
              setOpenAssignDriver(true);
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <IconTruck size={14} />
            <span>{t("Assign Driver")}</span>
          </button>,
        );
      }
      actions.push(
        <button
          key="upload-doc-ret"
          onClick={() => {
            setOpenDropdown(null);
            setSelectedPickup(row);
            setDoc(null);
            setOpenUploadDoc(true);
          }}
          className="flex w-full items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
        >
          <IconDownload size={14} />
          <span>{t("Upload Doc")}</span>
        </button>,
      );
    }

    if (row.type === "product" && canUpdateProduct) {
      if (
        row.status === "UPDATED" ||
        (row.createdByType === "CLIENT" && row.status === "MANIFEST_REQUEST")
      ) {
        actions.push(
          <button
            key="assign-staff-prod"
            onClick={() => {
              setOpenDropdown(null);
              setSelectedPickup(row);
              setOpenAssignCheckStaff(true);
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <IconPlus size={14} />
            <span>{t("Assign To Staff")}</span>
          </button>,
        );
      }
      if (
        row.status === "UPDATED" ||
        row.status === "ON_PROCESS" ||
        row.status === "ASSIGN_TO_STAFF" ||
        row.status === "ON_HOLD" ||
        row.status === "VERIFIED" ||
        (row.status === "MANIFEST_REQUEST" && row.createdByType === "CLIENT")
      ) {
        actions.push(
          <button
            key="check-verify-prod"
            onClick={() => {
              setOpenDropdown(null);
              setSelectedPickup(row);
              setProductViewType("update");
              setIsProductViewOpen(true);
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <IconEye size={14} />
            <span>{t("Checked & Verified")}</span>
          </button>,
        );
      }
      if (
        row.status === "VERIFIED" ||
        row.status === "ON_HOLD" ||
        row.status === "ASSIGN_TO_INVENTORY_STAFF" ||
        row.status === "ADD_INVENTORY"
      ) {
        actions.push(
          <button
            key="add-inventory-prod"
            onClick={() => {
              setOpenDropdown(null);
              setSelectedPickup(row);
              setOpenAddProductInventory(true);
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <IconPlus size={14} />
            <span>{t("Add Inventory")}</span>
          </button>,
        );
      }
      if (
        row.status === "VERIFIED" ||
        row.status === "ON_HOLD" ||
        row.status === "ON_PROCESS"
      ) {
        actions.push(
          <button
            key="assign-inv-staff-prod"
            onClick={() => {
              setOpenDropdown(null);
              setSelectedPickup(row);
              setOpenAssignInventoryStaff(true);
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <IconPlus size={14} />
            <span>{t("Assign Inventory Staff")}</span>
          </button>,
        );
      }
    }

    return actions;
  };

  const handleViewDetails = async (row: any) => {
    const targetType = row?.type;

    // Reset forms & order selections
    pickupReset();
    linehaulReset();
    returnReset();
    productReset();
    setSelectedOrders([]);
    setOpenDropdown(null);
    setSelectedPickupAwbIds([]);
    setSelectedAwbActions(null);

    // Only close other tabs if switching to a different manifest type
    setIsCreateOpen(false);
    if (targetType !== "pickup") setIsPickupViewOpen(false);
    if (targetType !== "linehaul") setIsLinehaulViewOpen(false);
    if (targetType !== "return") setIsReturnViewOpen(false);
    if (targetType !== "product") setIsProductViewOpen(false);

    setSelectedPickup(row);

    if (targetType === "pickup") {
      setIsPickupViewOpen(true);
    } else if (targetType === "linehaul") {
      setIsLinehaulViewOpen(true);
    } else if (targetType === "return") {
      await fetchReturnToShipperDetails(row.id);
      setIsReturnViewOpen(true);
    } else if (targetType === "product") {
      setProductViewType("view");
      setIsProductViewOpen(true);
    }
  };

  const [columnsData, setColumnsData] = useState([
    { text: "Checkbox", value: "checkbox", isShow: true },
    { text: "Manifest ID", value: "manifestId", isShow: true },
    { text: "Client", value: "client", isShow: true },
    { text: "Type", value: "type", isShow: true },
    // { text: "AWB/Item", value: "qty", isShow: true },
    { text: "CreatedBy", value: "createdBy", isShow: true },
    { text: "Date", value: "date", isShow: true },
    { text: "Status", value: "status", isShow: true },
    // { text: "Actions", value: "actions", isShow: true },
  ]);

  const handleSelectAll = () => {
    if (selectedRows.length === manifests.length) setSelectedRows([]);
    else setSelectedRows(manifests.map((m) => m.id));
  };

  const maxStatusWidth = React.useMemo(() => {
    if (!manifests.length) return "140px";
    const maxLen = manifests.reduce((max, m) => {
      const text = fmtStatus(m.status);
      return text.length > max ? text.length : max;
    }, 0);
    const calculatedWidth = Math.max(140, maxLen * 7.2 + 28);
    return `${calculatedWidth}px`;
  }, [manifests]);

  const columns = columnsData
    .filter((c) => c.isShow)
    .map((col) => {
      switch (col.value) {
        case "checkbox":
          return {
            label: <div className=" "></div>,
            render: (row: any) => {
              const hasChildren =
                row.type === "product" &&
                row.children &&
                row.children.length > 0;
              const isExpanded = expandedIdsRef.current.has(row.id);
              return (
                <div className="">
                  {hasChildren && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleRow(row.id);
                      }}
                      className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#ECECEC] bg-[#F7F6F2] hover:bg-gray-100 transition "
                      title={isExpanded ? t("Collapse") : t("Expand")}
                    >
                      {isExpanded ? (
                        <IconChevronUp size={16} />
                      ) : (
                        <IconChevronDown size={16} />
                      )}
                    </button>
                  )}
                </div>
              );
            },
          };

        case "manifestId":
          return {
            label: t("Manifest ID"),
            render: (row: any) => <span className=" ">{row.manifestId}</span>,
          };
        case "type":
          return {
            label: t("Type"),
            render: (row: any) => t(row.typeLabel),
          };
        case "client":
          return { label: t("Merchant"), render: (row: any) => t(row.client) };
        case "qty":
          return { label: t("AWB/Item"), render: (row: any) => row.qty };
        case "date":
          return {
            label: t("Date"),
            render: (row: any) => formatDateTime(row.date),
          };
        case "createdBy":
          return {
            label: t("Created By"),
            render: (row: any) => {
              return row.createdByName || t(row.createdByType) || "—";
            },
          };

        case "status":
          return {
            label: t("Status"),
            render: (row: any) => (
              <div className="flex justify-center">
                <span
                  className={`inline-flex items-center justify-center rounded-full py-1 px-4 text-xs font-semibold whitespace-nowrap text-center border shadow-sm select-none transition-all duration-150 hover:shadow ${STATUS_COLORS[row.status] || "bg-slate-50 border-slate-200 text-slate-700"}`}
                >
                  {t(fmtStatus(row.status))}
                </span>
              </div>
            ),
          };

        default:
          return { label: "", render: () => null };
      }
    });

  const handleExport = () => {
    const rows = manifests.map((m, i) => ({
      "S/N": i + 1,
      "Manifest ID": m.manifestId,
      Type: m.typeLabel,
      Client: m.client,
      Status: fmtStatus(m.status),
      "AWB/Item": m.qty,
      Date: formatDate(m.date),
      Resource: m.assignedResource,
    }));
    if (!rows.length) {
      toast.error("No data to export");
      return;
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Manifests");
    XLSX.writeFile(wb, "unified_manifests.xlsx");
    toast.success("Exported!");
  };

  // Creation Submissions
  const handlePickupCreate = (data: any) => {
    genratePickRequest(
      { ...data, shipmentsId: selectedOrders },
      {
        onSuccess: () => {
          setIsCreateOpen(false);
          pickupReset();
          setSelectedOrders([]);
          toast.success("Pickup Generated Successfully.");
          refetch();
        },
        onError: (error) => {
          toast.error(`${error}`);
        },
      },
    );
  };

  const handleLinehaulCreate = (formData: any) => {
    const containers = formData.linehaul_load_list || [];
    const formattedContainers = containers
      .map((c: any) => ({
        type: c.type,
        awbs: c.awbs
          .split(/[\n, ]+/)
          .map((a: string) => a.trim().toUpperCase())
          .filter(Boolean),
      }))
      .filter((c: any) => c.awbs.length > 0);

    if (!formattedContainers.length) {
      toast.error("Please add at least one container with AWBs");
      return;
    }

    const payload = {
      fromWarehouseId: formData.fromWarehouseId,
      toWarehouseId: formData.toWarehouseId,
      linehaul_load_list: formattedContainers,
    };

    sendCompanyRequest(payload, {
      onSuccess: () => {
        toast.success("Linehaul manifest created successfully");
        setIsCreateOpen(false);
        linehaulReset();
        refetch();
      },
      onError: (err: any) => {
        toast.error(err?.message || "Failed to create linehaul manifest");
      },
    });
  };

  const handleReturnCreate = (data: any) => {
    const payload = {
      clientId: data.clientId,
      requestType: data.requestType,
      shipmentsId: data.shipments
        .split(/[\n,\t]+/)
        .map((o: any) => o.trim())
        .filter((o: any) => o.length > 0),
    };
    genrateReturnToShipper(payload, {
      onSuccess: () => {
        setIsCreateOpen(false);
        returnReset();
        toast.success("Created Successfully.");
        refetch();
      },
      onError: (error) => {
        toast.error(`${error}`);
      },
    });
  };

  const handleProductCreate = (values: any) => {
    const payload = {
      forClientId: values.toClient,
      product: values.products?.map((product: any) => ({
        productId: product,
      })),
    };
    createPromo(payload, {
      onSuccess: () => {
        toast.success("Product Manifest Created");
        productReset();
        setIsCreateOpen(false);
        refetch();
      },
      onError: (err: any) => {
        toast.error(err?.message || "Failed to create manifest");
      },
    });
  };

  const isSubmitPending =
    (createType === "pickup" && genratePickRequestPending) ||
    (createType === "linehaul" && sendPending) ||
    (createType === "return" && genrateReturnToShipperPending) ||
    (createType === "product" && creatingProduct);

  const triggerSubmit = () => {
    if (createType === "pickup") {
      if (
        pickupWatch("pickupType") === "Shipment" &&
        selectedOrders.length === 0
      ) {
        toast.error("Please add at least one shipment");
        return;
      }
      handlePickupSubmit(handlePickupCreate)();
    } else if (createType === "linehaul") {
      handleLinehaulSubmit(handleLinehaulCreate)();
    } else if (createType === "return") {
      handleReturnSubmit(handleReturnCreate)();
    } else if (createType === "product") {
      handleProductSubmit(handleProductCreate)();
    }
  };

  // const renderExpandedContent = (row: any) => {
  //   if (row.type !== "product") return null;
  //   if (!expandedIdsRef.current.has(row.id)) return null;
  //   if (!row.children || row.children.length === 0) return null;

  //   return (
  //     <div className="bg-slate-50/50 border-t border-slate-100 px-6 py-3">
  //       <div className="relative pl-8">
  //         <div className="absolute left-4 top-2 bottom-6 w-0.5 bg-slate-200" />
  //         {row.children.map((child: any) => (
  //           <div key={child.id} className="relative mb-3 last:mb-0">
  //             <div className="absolute left-0 top-5 -translate-x-1/2 w-6 h-px bg-slate-200 z-10" />
  //             <div className="absolute left-4 top-5 -translate-x-1/2 w-2.5 h-2.5 rounded-full border-2 border-white bg-[#FBD037] z-20" />
  //             <div className="bg-white border border-slate-100 hover:border-[#FBD037]/50 rounded-xl p-3 px-4 pl-10 hover:shadow-sm transition-all duration-150">
  //               <div
  //                 onClick={(e) => {
  //                   e.stopPropagation();
  //                   handleViewDetails({ ...child, type: "product" });
  //                 }}
  //                 className="grid grid-cols-[3fr_3fr_3fr_3fr_3fr_3fr_3fr_3fr_3fr_3fr_3fr_1fr_1fr_0.5fr_3fr_] gap-4 items-center text-xs cursor-pointer"
  //               >
  //                 <div></div>
  //                 <div>
  //                   <span className="">
  //                     {child.manifestId || child.manifestShortId}
  //                   </span>
  //                 </div>
  //                 <div></div> <div></div> <div></div> <div></div> <div></div>{" "}
  //                 <div></div> <div></div>
  //                 <div>
  //                   <span className="text-slate-700">
  //                     {formatDateTime(child.date || child.createdAt)}
  //                   </span>
  //                 </div>
  //                 <div></div>
  //                 <div></div>
  //                 <div className="flex justify-center">
  //                   <span
  //                     className={`inline-flex items-center justify-center rounded-full px-3 py-1 text-[10px] font-semibold whitespace-nowrap border shadow-sm select-none ${STATUS_COLORS[child.status] || "bg-slate-50 border-slate-200 text-slate-700"}`}
  //                   >
  //                     {t(fmtStatus(child.status))}
  //                   </span>
  //                 </div>
  //               </div>
  //             </div>
  //           </div>
  //         ))}
  //       </div>
  //     </div>
  //   );
  // };

  const renderExpandedContent = (row: any) => {
    if (row.type !== "product") return null;
    if (!expandedIdsRef.current.has(row.id)) return null;
    if (!row.children?.length) return null;

    return (
      <div className="bg-slate-50 border-t border-slate-200 px-6 py-4">
        <div className="rounded-xl overflow-hidden border border-slate-200 bg-white">
          {/* Header */}

          <div className="grid grid-cols-4 bg-slate-100 text-xs font-semibold text-slate-600 px-4 py-3">
            <div>{t("Manifest")}</div>
            <div>{t("Created By")}</div>
            <div>{t("Date")}</div>
            <div>{t("Status")}</div>
          </div>

          {row.children.map((child: any) => (
            <div
              key={child.id}
              onClick={(e) => {
                e.stopPropagation();
                handleViewDetails({
                  ...child,
                  type: "product",
                });
              }}
              className="
              grid
              grid-cols-4
              items-center
              px-4
              py-3
              border-t
              hover:bg-yellow-50
              cursor-pointer
              transition
            "
            >
              <div>
                <p className="text-sm text-slate-600">
                  {child.manifestId}
                </p>
              </div>

              <div className="text-sm text-slate-600">
                {child.createdBy}
              </div>
              <div className="text-sm text-slate-600">
                {formatDateTime(child.createdAt || child.date)}
              </div>

              <div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    STATUS_COLORS[child.status]
                  }`}
                >
                  {fmtStatus(child.status)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  const showRightPanel =
    isPickupViewOpen ||
    isLinehaulViewOpen ||
    isReturnViewOpen ||
    isProductViewOpen ||
    isCreateOpen;

  return (
    <>
     <div className="grid grid-cols-12 gap-6 p-2  bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 w-ful rounded-r-3xl rounded-bl-3xl w-full">
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        <div
          className={
            showRightPanel ? "col-span-12 xl:col-span-7" : "col-span-12"
          }
        >
          <div
            className="bg-white rounded-3xl overflow-visible"
          >
            {/* Header */}
            <div className="px-6 py-1.5">
              <div className="flex flex-wrap items-center justify-between gap-4">
                {/* Left side: Brand badge & Type Selector */}
                <div className="flex flex-wrap items-center gap-3">
                   
                </div>

                {/* Right side: Action controls */}
                <div className="flex flex-wrap items-center gap-2.5 sm:justify-end">
                  <NewSelectInput
                    name="type-filter"
                    placeholder="Type"
                    value={typeFilter}
                    setValue={(val) => {
                      setTypeFilter(val || "");
                      setPageNo(1);
                    }}
                    selectOptions={filterOptions}
                    className="w-fit"
                    removeDiv
                    compact
                  />
                  {(typeFilter || searchFor) && (
                    <button
                      onClick={() => {
                        setTypeFilter("");
                        setSearchFor("");
                        setShowSearch(false);
                        setPageNo(1);
                      }}
                      className="w-8 h-8 flex items-center justify-center border border-slate-200 text-[#212121] rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer disabled:opacity-50"
                      title={t("Reset Filters")}
                    >
                      <IconRotateClockwise size={14} stroke={2.5} />
                    </button>
                  )}
                  {/* Search */}
                  <div className="relative flex items-center group">
                    <div
                      onClick={() => !showSearch && setShowSearch(true)}
                      className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${showSearch ? "w-64 px-2 cursor-default" : "w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95"}`}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowSearch(!showSearch);
                        }}
                        className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                      >
                        <IconSearch size={17} stroke={2.5} />
                      </button>
                      {showSearch && (
                        <>
                          <input
                            type="text"
                            value={searchFor}
                            onChange={(e) => {
                              setSearchFor(e.target.value);
                              setPageNo(1);
                            }}
                            placeholder="Search..."
                            autoFocus
                            className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none focus:outline-none focus:ring-0"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSearchFor("");
                              setShowSearch(false);
                            }}
                            className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                            title={t("Clear & Close")}
                          >
                            <IconX size={14} stroke={2.5} />
                          </button>
                        </>
                      )}
                    </div>
                    {!showSearch && (
                      <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                        {t("Search")}
                      </div>
                    )}
                  </div>

                  {/* Export */}
                  <div className="relative group">
                    <button
                      onClick={handleExport}
                      className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
                    >
                      <IconDownload size={17} stroke={2.5} />
                    </button>
                    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                      {t("Export")}
                    </div>
                  </div>

                  {/* Entries Selector */}
                  {data && data.total > 10 && (
                    <EntriesSelector
                      value={limit}
                      options={[
                        10,
                        ...(data.total > 10 ? [20] : []),
                        ...(data.total > 20 ? [30] : []),
                        ...(data.total > 50 ? [50] : []),
                        ...(data.total > 100 ? [100] : []),
                      ]}
                      onChange={(val) => {
                        setLimit(val);
                        setPageNo(1);
                        setSelectedRows([]);
                      }}
                    />
                  )}

                  {/* Columns Filter */}
                  <div className="relative group">
                    <ColumnsFilter
                      columnsData={columnsData.filter(
                        (c) => c.value !== "checkbox",
                      )}
                      setHeadings={(val, checked) =>
                        setColumnsData((prev) =>
                          prev.map((c) =>
                            c.value === val ? { ...c, isShow: checked } : c,
                          ),
                        )
                      }
                    />
                    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                      {t("Columns View")}
                    </div>
                  </div>

                  {/* Create Manifest button in toolbar */}
                  {canCreateAnyManifest && (
                    <div className="relative group">
                      <button
                        onClick={() => {
                          setIsPickupViewOpen(false);
                          setIsLinehaulViewOpen(false);
                          setIsReturnViewOpen(false);
                          setIsProductViewOpen(false);
                          setCreateType("");
                          setIsCreateOpen(true);
                        }}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-150 border shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer ${
                          isCreateOpen
                            ? "bg-white hover:bg-slate-50 border-slate-200 text-slate-700"
                            : "bg-[#FBD037] border-[#FBD037] text-[#131327] hover:bg-[#e2bd30]"
                        }`}
                      >
                        <IconPlus size={17} stroke={2.5} />
                      </button>
                      <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-[10px] font-bold px-2 py-1 rounded shadow-md whitespace-nowrap pointer-events-none z-50">
                        {t("Create Manifest")}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="border-t border-slate-100">
              {isLoading ? (
                <div>
                  <TableSkeleton
                    columns={columnsData
                      .filter((c) => c.isShow)
                      .map((c) => c.text)}
                    rows={6}
                  />
                </div>
              ) : manifests.length > 0 ? (
                <ListView
                  onRowClick={(row) => handleViewDetails(row)}
                  data={manifests}
                  columns={columns}
                  // thClassName={
                  //   showRightPanel
                  //     ? "[&_th]:!px-1.5 md:[&_th]:!px-2 xl:[&_th]:!px-2.5"
                  //     : ""
                  // }
                  // tdClassName={
                  //   showRightPanel ? "!px-1.5 md:!px-2 xl:!px-2.5" : ""
                  // }
                  // scrollbarClassName={
                  //   showRightPanel ? "custom-scrollbar" : "hide-scrollbar"
                  // }
                  renderExpandedContent={renderExpandedContent}
                  expandedRows={{ has: () => true } as any}
                />
              ) : (
                <div className="py-16 text-center">
                  <NoDataFound />
                </div>
              )}
            </div>
          </div>
        </div>

        {showRightPanel && (
          <>
            <div
              className="fixed inset-0 bg-black/40 z-30 xl:hidden"
              onClick={() => {
                setIsPickupViewOpen(false);
                setIsLinehaulViewOpen(false);
                setIsReturnViewOpen(false);
                setIsProductViewOpen(false);
                setIsCreateOpen(false);
                pickupReset();
                linehaulReset();
                returnReset();
                productReset();
                setSelectedOrders([]);
                setSelectedPickupAwbIds([]);
                setSelectedAwbActions(null);
              }}
            />
            <div className="col-span-12 xl:col-span-5 space-y-4 bg-[#F7F6F2] xl:static fixed inset-y-0 right-0 z-[35] w-full sm:w-[450px] xl:w-auto p-4 pt-[76px] sm:pt-[96px] xl:p-0 shadow-2xl xl:shadow-none   xl:bg-transparent overflow-y-auto xl:overflow-visible flex flex-col justify-start">
              {isPickupViewOpen && selectedPickup && (
                <div className="relative rounded-3xl border border-gray-200 shadow-sm flex flex-col overflow-hidden bg-white w-full h-auto  xl:sticky xl:top-[90px]">
                  <div className=" px-6 py-4 border-b border-gray-200 bg-white space-y-2">
                    <div className="flex items-start justify-between">
                      <h2 className="text-lg font-semibold text-[#212121]/60 truncate">
                        {selectedPickup?.pickupId || selectedPickup?.manifestId}
                      </h2>

                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-bold border ${
                            selectedPickup.status === "COMPLETED"
                              ? "bg-green-50 text-green-700 border-green-200"
                              : "bg-blue-50 text-blue-700 border-blue-200"
                          }`}
                        >
                          {t(fmtStatus(selectedPickup.status))}
                        </span>
                        <button
                          onClick={() => {
                            setIsPickupViewOpen(false);
                            setSelectedPickupAwbIds([]);
                            setSelectedAwbActions(null);
                          }}
                          className="p-1 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors"
                        >
                          <IconX size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <p className="break-all px-2.5 py-0.5 bg-gray-100 border rounded-full text-xs text-[#212121] font-medium">
                          {t("Type")} : {t("Pickup")}
                        </p>
                        <p className="break-all px-2.5 py-0.5 bg-gray-100 border rounded-full text-xs text-[#212121] font-medium">
                          {t("Date")} :{" "}
                          {formatDate(
                            selectedPickup.pickupDate ||
                              selectedPickup.date ||
                              selectedPickup.createdAt,
                          )}
                        </p>
                        <p className="break-all px-2.5 py-0.5 bg-gray-100 border rounded-full text-xs text-[#212121] font-medium">
                          {t("Time")} :{" "}
                          {(() => {
                            const slot = selectedPickup.timeSlot;
                            if (!slot) return "—";
                            const date = new Date(slot);
                            if (!isNaN(date.getTime()) && slot.includes("T")) {
                              return formatTime(slot);
                            }
                            return slot;
                          })()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {!openAssignDriver &&
                        !openPickupCollect &&
                        !openAssignReceiver &&
                        !openRiwVerification ? (
                          selectedAwbActions ? (
                            <>
                              {selectedAwbActions.type === "assign" && (
                                <button
                                  key="assign-driver-selected"
                                  onClick={() => {
                                    assignDriverReset();
                                    setOpenAssignDriver(true);
                                  }}
                                  className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors shadow-sm hover:shadow active:scale-[0.98]"
                                >
                                  {t("Assign Driver")}
                                </button>
                              )}
                              {selectedAwbActions.type === "collect" && (
                                <button
                                  key="collect-selected"
                                  onClick={() => {
                                    setSelectedRows(selectedAwbActions.ids);
                                    setOpenPickupCollect(true);
                                  }}
                                  className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors shadow-sm hover:shadow active:scale-[0.98]"
                                >
                                  {t("Collect")}
                                </button>
                              )}
                              {selectedAwbActions.type === "inbound" && (
                                <button
                                  key="inbound-selected"
                                  onClick={() => {
                                    setSelectedRows(selectedAwbActions.ids);
                                    setMissingToggle(false);
                                    setDamageToggle(false);
                                    setMissingMessage("");
                                    setDamageMessage("");
                                    setAwbNumbers([]);
                                    setOpenRiwVerification(true);
                                  }}
                                  className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors shadow-sm hover:shadow active:scale-[0.98]"
                                >
                                  {t("Inbound")}
                                </button>
                              )}
                              {selectedAwbActions.type === "assign-staff" && (
                                <>
                                  <button
                                    key="assign-staff-selected"
                                    onClick={() => {
                                      assignReceiverReset();
                                      setOpenAssignReceiver(true);
                                    }}
                                    className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors shadow-sm hover:shadow active:scale-[0.98]"
                                  >
                                    {t("Assign Staff")}
                                  </button>
                                  <button
                                    key="inbound-collected-selected"
                                    onClick={() => {
                                      setSelectedRows(selectedAwbActions.ids);
                                      setMissingToggle(false);
                                      setDamageToggle(false);
                                      setMissingMessage("");
                                      setDamageMessage("");
                                      setAwbNumbers([]);
                                      setOpenRiwVerification(true);
                                    }}
                                    className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors shadow-sm hover:shadow active:scale-[0.98]"
                                  >
                                    {t("Inbound")}
                                  </button>
                                </>
                              )}
                            </>
                          ) : (
                            <>{renderViewHeaderActions(selectedPickup)}</>
                          )
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                if (openAssignDriver)
                                  handleAssignDriverSubmit(
                                    handleAssignDriver,
                                  )();
                                if (openPickupCollect)
                                  handlePickupCollectSubmitForm(
                                    handlePickupCollected,
                                  )();
                                if (openAssignReceiver)
                                  handleAssignReceiverSubmitForm(
                                    handleAssignReceiver,
                                  )();
                                if (openRiwVerification) handlePickupInbound();
                              }}
                              disabled={
                                openPickupCollect || openRiwVerification
                                  ? selectedRows.length === 0
                                  : false
                              }
                              className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isAssignPickupDriverPending ||
                              isPickCollectedPending ||
                              isAssignPickupReceiverPending ||
                              isPickRequestInboundPending ? (
                                <IconLoader2
                                  size={12}
                                  className="animate-spin"
                                />
                              ) : (
                                <span>
                                  {openPickupCollect
                                    ? t("Collect")
                                    : openRiwVerification
                                      ? t("Inbound")
                                      : t("Assign")}
                                </span>
                              )}
                            </button>
                            <button
                              onClick={() => {
                                setOpenAssignDriver(false);
                                setOpenPickupCollect(false);
                                setOpenAssignReceiver(false);
                                setOpenRiwVerification(false);
                                assignDriverReset();
                                assignReceiverReset();
                                pickupCollectReset();
                                setSelectedRows([]);
                                setSelectedPickupAwbIds([]);
                                setSelectedAwbActions(null);
                              }}
                              className="px-3 py-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-xs font-semibold text-slate-600 hover:text-slate-900"
                            >
                              {t("Cancel")}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    {openAssignDriver ? (
                      <div className="p-4">
                        <AssignDriver
                          register={assignDriverRegister}
                          errors={assignDriverErrors}
                          setValue={assignDriverSetValue as any}
                          watch={assignDriverWatch}
                          clearErrors={assignDriverClearErrors}
                          control={assignDriverControl}
                          reset={assignDriverReset}
                          pickupItem={selectedPickup}
                        />
                      </div>
                    ) : openPickupCollect ? (
                      <div className="p-4">
                        <PickupCollectModal
                          pickupId={selectedPickup?.pickupId}
                          mode="from-row"
                          selectedRows={selectedRows}
                          setSelectedRows={setSelectedRows}
                          register={pickupCollectRegister}
                          setValue={pickupCollectSetValue}
                          watch={pickupCollectWatch}
                          errors={pickupCollectErrors}
                          reset={pickupCollectReset}
                        />
                      </div>
                    ) : openAssignReceiver ? (
                      <div className="p-4">
                        <AssignReceiverModal
                          register={assignReceiverRegister}
                          errors={assignReceiverErrors}
                          setValue={assignReceiverSetValue as any}
                          watch={assignReceiverWatch}
                          clearErrors={assignReceiverClearErrors}
                          control={assignReceiverControl}
                          pickup={selectedPickup}
                        />
                      </div>
                    ) : openRiwVerification ? (
                      <div className="p-4">
                        <RiwVerificationModal
                          pickup={selectedPickup}
                          selectedRows={selectedRows}
                          setSelectedRows={setSelectedRows}
                          missingToggle={missingToggle}
                          setMissingToggle={setMissingToggle}
                          damageToggle={damageToggle}
                          setDamageToggle={setDamageToggle}
                          missingMessage={missingMessage}
                          setMissingMessage={setMissingMessage}
                          damageMessage={damageMessage}
                          setDamageMessage={setDamageMessage}
                          type={selectedPickup?.type}
                        />
                      </div>
                    ) : (
                      <PickupViewModal
                        open={isPickupViewOpen}
                        onClose={() => setIsPickupViewOpen(false)}
                        pickup={selectedPickup}
                        isSidebar={true}
                        selectedIds={selectedPickupAwbIds}
                        setSelectedIds={setSelectedPickupAwbIds}
                        onSelectionActionsChange={setSelectedAwbActions}
                        onOpenAssignDriver={() => {
                          assignDriverReset();
                          setOpenAssignDriver(true);
                        }}
                        onOpenPickupCollect={(ids) => {
                          setSelectedRows(ids);
                          setOpenPickupCollect(true);
                        }}
                        onOpenAssignReceiver={() => {
                          assignReceiverReset();
                          setOpenAssignReceiver(true);
                        }}
                        onOpenRiwVerification={(ids) => {
                          setSelectedRows(ids);
                          setMissingToggle(false);
                          setDamageToggle(false);
                          setMissingMessage("");
                          setDamageMessage("");
                          setAwbNumbers([]);
                          setOpenRiwVerification(true);
                        }}
                      />
                    )}
                  </div>
                </div>
              )}

              {isLinehaulViewOpen && selectedPickup && (
                <div className="relative rounded-3xl border border-gray-200 shadow-sm flex flex-col overflow-hidden bg-white w-full h-auto xl:h-[calc(100vh-115px)] max-h-[calc(100vh-115px)] xl:sticky xl:top-[90px]">
                  <div className=" px-6 py-4 border-b border-gray-200 bg-white space-y-2">
                    <div className="flex items-start justify-between">
                      <h2 className="text-lg font-semibold text-[#212121]/60 truncate">
                        {selectedPickup?.customId || selectedPickup?.manifestId}
                      </h2>

                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-bold border ${
                            selectedPickup.status === "COMPLETED"
                              ? "bg-green-50 text-green-700 border-green-200"
                              : "bg-blue-50 text-blue-700 border-blue-200"
                          }`}
                        >
                          {t(fmtStatus(selectedPickup.status))}
                        </span>
                        <button
                          onClick={() => setIsLinehaulViewOpen(false)}
                          className="p-1 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors"
                        >
                          <IconX size={14} />
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <p className="break-all px-2.5 py-0.5 bg-gray-100 border rounded-full text-xs text-[#212121] font-medium">
                          {t("Type")} : {t("Linehaul Manifest")}
                        </p>
                        <p className="break-all px-2.5 py-0.5 bg-gray-100 border rounded-full text-xs text-[#212121] font-medium">
                          {t("Date")} :{" "}
                          {formatDate(
                            selectedPickup.pickupDate ||
                              selectedPickup.date ||
                              selectedPickup.createdAt,
                          )}
                        </p>
                        <p className="break-all px-2.5 py-0.5 bg-gray-100 border rounded-full text-xs text-[#212121] font-medium">
                          {t("Time")} : {selectedPickup.timeSlot}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {!openAssignLinehaulVehicle &&
                        !openAssignReceiverLinehaul &&
                        !openRiwVerification ? (
                          <>
                            {renderViewHeaderActions(selectedPickup)}
                            {/* <button
                            onClick={() => setIsLinehaulViewOpen(false)}
                            className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors"
                          >
                            <IconX size={18} />
                          </button> */}
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                if (openAssignLinehaulVehicle)
                                  handleAssignLinehaulSubmitForm(
                                    handleAssignLinehaulVehicleSubmit,
                                  )();
                                if (openAssignReceiverLinehaul)
                                  handleAssignReceiverSubmitForm(
                                    handleAssignLinehaulReceiverSubmit,
                                  )();
                                if (openRiwVerification)
                                  handleLinehaulInbound();
                              }}
                              className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5"
                            >
                              {isAssignLinehaulVehiclePending ||
                              isAssignLinehaulReceiverPending ||
                              isLinehaulRequestInboundPending ? (
                                <IconLoader2
                                  size={12}
                                  className="animate-spin"
                                />
                              ) : (
                                <span>{t("Save")}</span>
                              )}
                            </button>
                            <button
                              onClick={() => {
                                setOpenAssignLinehaulVehicle(false);
                                setOpenAssignReceiverLinehaul(false);
                                setOpenRiwVerification(false);
                                assignLinehaulReset();
                                assignReceiverReset();
                                setSelectedRows([]);
                              }}
                              className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-xs font-semibold text-slate-600 hover:text-slate-900"
                            >
                              {t("Cancel")}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
                    <h2 className="text-lg font-semibold text-[#212121] truncate">
                      {openAssignLinehaulVehicle
                        ? "Assign Vehicle"
                        : openAssignReceiverLinehaul
                          ? "Assign Receiver"
                          : openRiwVerification
                            ? "RIW Verification"
                            : selectedPickup?.customId ||
                              selectedPickup?.manifestId}
                    </h2>
                    
                    <div className="flex items-center gap-2">
                      {!openAssignLinehaulVehicle &&
                      !openAssignReceiverLinehaul &&
                      !openRiwVerification ? (
                        <>
                          {renderViewHeaderActions(selectedPickup)}
                          <button
                            onClick={() => setIsLinehaulViewOpen(false)}
                            className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors"
                          >
                            <IconX size={18} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => {
                              if (openAssignLinehaulVehicle)
                                handleAssignLinehaulSubmitForm(
                                  handleAssignLinehaulVehicleSubmit,
                                )();
                              if (openAssignReceiverLinehaul)
                                handleAssignReceiverSubmitForm(
                                  handleAssignLinehaulReceiverSubmit,
                                )();
                              if (openRiwVerification) handleLinehaulInbound();
                            }}
                            className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5"
                          >
                            {isAssignLinehaulVehiclePending ||
                            isAssignLinehaulReceiverPending ||
                            isLinehaulRequestInboundPending ? (
                              <IconLoader2 size={12} className="animate-spin" />
                            ) : (
                              <span>{t("Save")}</span>
                            )}
                          </button>
                          <button
                            onClick={() => {
                              setOpenAssignLinehaulVehicle(false);
                              setOpenAssignReceiverLinehaul(false);
                              setOpenRiwVerification(false);
                              assignLinehaulReset();
                              assignReceiverReset();
                              setSelectedRows([]);
                            }}
                            className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-xs font-semibold text-slate-600 hover:text-slate-900"
                          >
                            {t("Cancel")}
                          </button>
                        </>
                      )}
                    </div>
                  </div> */}
                  <div className="flex-1 overflow-y-auto">
                    {openAssignLinehaulVehicle ? (
                      <div className="p-4">
                        <AssignLineHoulVehicleModal
                          register={assignLinehaulRegister}
                          errors={assignLinehaulErrors}
                          setValue={assignLinehaulSetValue as any}
                          watch={assignLinehaulWatch}
                          clearErrors={assignLinehaulClearErrors}
                          pickup={selectedPickup}
                        />
                      </div>
                    ) : openAssignReceiverLinehaul ? (
                      <div className="p-4">
                        <AssignReceiverLineHaulManifestModal
                          register={assignReceiverRegister}
                          errors={assignReceiverErrors}
                          setValue={assignReceiverSetValue as any}
                          watch={assignReceiverWatch}
                          clearErrors={assignReceiverClearErrors}
                          control={assignReceiverControl}
                          pickup={selectedPickup}
                        />
                      </div>
                    ) : openRiwVerification ? (
                      <div className="p-4">
                        <RiwVerificationModal
                          pickup={selectedPickup}
                          selectedRows={selectedRows}
                          setSelectedRows={setSelectedRows}
                          missingToggle={missingToggle}
                          setMissingToggle={setMissingToggle}
                          damageToggle={damageToggle}
                          setDamageToggle={setDamageToggle}
                          missingMessage={missingMessage}
                          setMissingMessage={setMissingMessage}
                          damageMessage={damageMessage}
                          setDamageMessage={setDamageMessage}
                          type={selectedPickup?.type}
                        />
                      </div>
                    ) : (
                      <LineHaulViewModal
                        open={isLinehaulViewOpen}
                        onClose={() => setIsLinehaulViewOpen(false)}
                        pickup={selectedPickup}
                        isSidebar={true}
                      />
                    )}
                  </div>
                </div>
              )}

              {isReturnViewOpen && selectedPickup && (
                <div className="relative rounded-3xl border border-gray-200 shadow-sm flex flex-col overflow-hidden bg-white w-full h-auto xl:h-[calc(100vh-115px)] max-h-[calc(100vh-115px)] xl:sticky xl:top-[90px]">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
                    <h2 className="text-lg font-semibold text-[#212121] truncate">
                      {openAssignDriver
                        ? t("Assign Driver")
                        : openUploadDoc
                          ? t("Upload Document")
                          : selectedPickup?.manifestId || t("Return Details")}
                    </h2>
                    <div className="flex items-center gap-2">
                      {!openAssignDriver && !openUploadDoc ? (
                        <>
                          {renderViewHeaderActions(selectedPickup)}
                          <button
                            onClick={() => setIsReturnViewOpen(false)}
                            className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors"
                          >
                            <IconX size={18} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => {
                              if (openAssignDriver)
                                handleAssignDriverSubmit(
                                  handleAssignReturnDriverSubmit,
                                )();
                              if (openUploadDoc) handleUploadReturnDoc();
                            }}
                            className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5"
                          >
                            {isAssignReturnDriverPending ||
                            isUploadReturnDocPending ? (
                              <IconLoader2 size={12} className="animate-spin" />
                            ) : (
                              <span>Save</span>
                            )}
                          </button>
                          <button
                            onClick={() => {
                              setOpenAssignDriver(false);
                              setOpenUploadDoc(false);
                              assignDriverReset();
                              setDoc(null);
                            }}
                            className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-xs font-semibold text-slate-600 hover:text-slate-900"
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    {openAssignDriver ? (
                      <div className="p-4">
                        <AssignDriver
                          register={assignDriverRegister}
                          errors={assignDriverErrors}
                          setValue={assignDriverSetValue as any}
                          watch={assignDriverWatch}
                          clearErrors={assignDriverClearErrors}
                          control={assignDriverControl}
                          reset={assignDriverReset}
                          pickupItem={selectedPickup}
                        />
                      </div>
                    ) : openUploadDoc ? (
                      <div className="p-4">
                        <UploadModal setDoc={setDoc} className="w-full" />
                      </div>
                    ) : (
                      <ViewDetailsReturnToShipperModal
                        open={isReturnViewOpen}
                        onClose={() => setIsReturnViewOpen(false)}
                        pickup={selectedPickup}
                        returnToShipperDetails={returnToShipperDetails}
                        isSidebar={true}
                        onUploadDoc={() => {
                          setDoc(null);
                          setOpenUploadDoc(true);
                        }}
                      />
                    )}
                  </div>
                </div>
              )}

              {isProductViewOpen && selectedPickup && (
                <div className="relative rounded-3xl border border-gray-200 shadow-sm flex flex-col overflow-hidden bg-white w-full h-auto xl:h-[calc(100vh-115px)] max-h-[calc(100vh-115px)] xl:sticky xl:top-[90px]">
                  {/* <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
                    <h2 className="text-lg font-semibold text-[#212121] truncate">
                      {openAssignCheckStaff
                        ? "Assign To Staff"
                        : openAssignInventoryStaff
                          ? "Assign Inv Staff"
                          : openAddProductInventory
                            ? "Add Inventory"
                            : productViewType === "update"
                              ? "Verify Manifest"
                              : selectedPickup?.manifestId || "Product Details"}
                    </h2>
                    <div className="flex items-center gap-2">
                      {!openAssignCheckStaff &&
                      !openAssignInventoryStaff &&
                      !openAddProductInventory &&
                      productViewType !== "update" ? (
                        <>
                          {renderViewHeaderActions(selectedPickup)}
                          <button
                            onClick={() => setIsProductViewOpen(false)}
                            className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors"
                          >
                            <IconX size={18} />
                          </button>
                        </>
                      ) : (
                        <>
                          {productViewType === "update" && (
                            <button
                              type="submit"
                              form="product-verify-form"
                              className="px-3 py-1.5 bg-[#FBD037] hover:bg-yellow-400 text-black rounded-lg text-xs font-semibold transition-colors shadow-sm hover:shadow"
                            >
                              Confirm
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setOpenAssignCheckStaff(false);
                              setOpenAssignInventoryStaff(false);
                              setOpenAddProductInventory(false);
                              if (productViewType === "update") {
                                setProductViewType("view");
                              }
                            }}
                            className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-xs font-semibold text-slate-600 hover:text-slate-900"
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </div> */}
                  <div className=" px-6 py-4 border-b border-gray-200 bg-white space-y-2">
                    <div className="flex items-start justify-between">
                      <h2 className="text-lg font-semibold text-[#212121]/60 truncate">
                        {selectedPickup?.pickupId || selectedPickup?.manifestId}
                      </h2>

                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-bold border ${
                            selectedPickup.status === "COMPLETED"
                              ? "bg-green-50 text-green-700 border-green-200"
                              : "bg-blue-50 text-blue-700 border-blue-200"
                          }`}
                        >
                          {t(fmtStatus(selectedPickup.status))}
                        </span>

                        <button
                          onClick={() => setIsProductViewOpen(false)}
                          className="p-1 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors"
                        >
                          <IconX size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <p className="break-all px-2.5 py-0.5 bg-gray-100 border rounded-full text-xs text-[#212121] font-medium">
                          {t("Type")} : {t("Product")}
                        </p>
                        <p className="break-all px-2.5 py-0.5 bg-gray-100 border rounded-full text-xs text-[#212121] font-medium">
                          {t("Date")} :{" "}
                          {formatDate(
                            selectedPickup.pickupDate ||
                              selectedPickup.date ||
                              selectedPickup.createdAt,
                          )}
                        </p>
                        <p className="break-all px-2.5 py-0.5 bg-gray-100 border rounded-full text-xs text-[#212121] font-medium">
                          {t("Time")} : {selectedPickup.timeSlot}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {!openAssignDriver &&
                        !openPickupCollect &&
                        !openAssignReceiver &&
                        !openRiwVerification ? (
                          selectedAwbActions ? (
                            <>
                              {selectedAwbActions.type === "assign" && (
                                <button
                                  key="assign-driver-selected"
                                  onClick={() => {
                                    assignDriverReset();
                                    setOpenAssignDriver(true);
                                  }}
                                  className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors shadow-sm hover:shadow active:scale-[0.98]"
                                >
                                  {t("Assign Driver")}
                                </button>
                              )}
                              {selectedAwbActions.type === "collect" && (
                                <button
                                  key="collect-selected"
                                  onClick={() => {
                                    setSelectedRows(selectedAwbActions.ids);
                                    setOpenPickupCollect(true);
                                  }}
                                  className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors shadow-sm hover:shadow active:scale-[0.98]"
                                >
                                  {t("Collect")}
                                </button>
                              )}
                              {selectedAwbActions.type === "inbound" && (
                                <button
                                  key="inbound-selected"
                                  onClick={() => {
                                    setSelectedRows(selectedAwbActions.ids);
                                    setMissingToggle(false);
                                    setDamageToggle(false);
                                    setMissingMessage("");
                                    setDamageMessage("");
                                    setAwbNumbers([]);
                                    setOpenRiwVerification(true);
                                  }}
                                  className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors shadow-sm hover:shadow active:scale-[0.98]"
                                >
                                  {t("Inbound")}
                                </button>
                              )}
                            </>
                          ) : (
                            <>{renderViewHeaderActions(selectedPickup)}</>
                          )
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                if (openAssignDriver)
                                  handleAssignDriverSubmit(
                                    handleAssignDriver,
                                  )();
                                if (openPickupCollect)
                                  handlePickupCollectSubmitForm(
                                    handlePickupCollected,
                                  )();
                                if (openAssignReceiver)
                                  handleAssignReceiverSubmitForm(
                                    handleAssignReceiver,
                                  )();
                                if (openRiwVerification) handlePickupInbound();
                              }}
                              disabled={
                                openPickupCollect || openRiwVerification
                                  ? selectedRows.length === 0
                                  : false
                              }
                              className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isAssignPickupDriverPending ||
                              isPickCollectedPending ||
                              isAssignPickupReceiverPending ||
                              isPickRequestInboundPending ? (
                                <IconLoader2
                                  size={12}
                                  className="animate-spin"
                                />
                              ) : (
                                <span>
                                  {openPickupCollect
                                    ? t("Collect")
                                    : openRiwVerification
                                      ? t("Inbound")
                                      : t("Assign")}
                                </span>
                              )}
                            </button>
                            <button
                              onClick={() => {
                                setOpenAssignDriver(false);
                                setOpenPickupCollect(false);
                                setOpenAssignReceiver(false);
                                setOpenRiwVerification(false);
                                assignDriverReset();
                                assignReceiverReset();
                                pickupCollectReset();
                                setSelectedRows([]);
                                setSelectedPickupAwbIds([]);
                                setSelectedAwbActions(null);
                              }}
                              className="px-3 py-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-xs font-semibold text-slate-600 hover:text-slate-900"
                            >
                              {t("Cancel")}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    {openAssignCheckStaff ? (
                      <AssignCheckStaff
                        open={openAssignCheckStaff}
                        onClose={() => setOpenAssignCheckStaff(false)}
                        manifest={selectedPickup}
                        fetchData={refetch}
                        isSidebar={true}
                      />
                    ) : openAssignInventoryStaff ? (
                      <AssignInventoryStaff
                        open={openAssignInventoryStaff}
                        onClose={() => setOpenAssignInventoryStaff(false)}
                        manifest={selectedPickup}
                        fetchData={refetch}
                        isSidebar={true}
                      />
                    ) : openAddProductInventory ? (
                      <AddProductInventoryModal
                        open={openAddProductInventory}
                        onClose={() => setOpenAddProductInventory(false)}
                        id={selectedPickup?.id}
                        fetchData={refetch}
                        isSidebar={true}
                      />
                    ) : (
                      <ViewProductRequestModal
                        open={isProductViewOpen}
                        onClose={() => setIsProductViewOpen(false)}
                        id={selectedPickup?.id}
                        fetchData={refetch}
                        type={productViewType}
                        isSidebar={true}
                      />
                    )}
                  </div>
                </div>
              )}

              {isCreateOpen && (
                <div className="relative bg-white rounded-3xl shadow-sm border flex flex-col overflow-hidden w-full h-auto max-h-[calc(100vh-115px)] xl:sticky xl:top-[90px]">
                  {/* Header */}
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
                    <div>
                      <h2 className="text-xl font-semibold text-[#212121]">
                        {t("Create Manifest")}
                      </h2>
                    </div>
                    <div className="flex items-center gap-3">
                      <NewSelectInput
                        className="w-full"
                        label=""
                        name="createType"
                        value={createType || ""}
                        onChange={(val) => setCreateType(val)}
                        selectOptions={createOptions}
                        placeholder="Select"
                        forceDownward={true}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          triggerSubmit();
                        }}
                        disabled={isSubmitPending || !createType}
                        className="px-4 py-2 w-f bg-[#FBD037] text-[#212121] rounded-lg disabled:opacity-50 text-xs font-semibold transition-colors flex items-center gap-1.5 hover:bg-[#e6be32]"
                      >
                        {isSubmitPending ? (
                          <IconLoader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <span>{t("Create")}</span>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setIsCreateOpen(false);
                          pickupReset();
                          linehaulReset();
                          returnReset();
                          productReset();
                          setSelectedOrders([]);
                        }}
                        className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors"
                      >
                        <IconX size={18} />
                      </button>
                    </div>
                  </div>

                  {/* Form fields depending on selected type */}
                  <div className="flex-1 overflow-y-auto min-h-0 space-y-4 p-4 scrollbar-clean">
                    {/* <div className="relative group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-1.5">
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                      <div className="flex items-center justify-between  border-b border-[#FBD037]/30 pb-1.5 mb-2">
                        <SectionHeader
                          className="w-full"
                          icon={IconTruck}
                          title="Manifest"
                        />
                        <NewSelectInput
                          className="w-full"
                          label=""
                          name="createType"
                          value={createType || ""}
                          onChange={(val) => setCreateType(val)}
                          selectOptions={[
                            { value: "pickup", text: "Shipment" },
                            { value: "product", text: "Product" },
                            { value: "return", text: "Return" },
                            { value: "linehaul", text: "Line Haul" },
                          ]}
                          placeholder="Select"
                          forceDownward={true}
                        />
                      </div>
                    </div> */}
                    {!createType && (
                      <div className="flex flex-col items-center justify-center h-full p-8 text-center min-h-[300px]">
                        <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4 text-slate-400">
                          <IconPackage size={32} />
                        </div>
                        <h3 className="text-base font-semibold text-slate-700">
                          {t("Select Manifest Type")}
                        </h3>
                        <p className="text-xs text-slate-400 max-w-[240px] mt-1">
                          {t("Choose a manifest type from the dropdown above to configure your new manifest details.")}
                        </p>
                      </div>
                    )}
                    {createType === "pickup" && (
                      <GeneratePickupModal
                        register={pickupRegister}
                        errors={pickupErrors}
                        clearErrors={pickupClearErrors}
                        setValue={pickupSetValue}
                        control={pickupControl}
                        selectedOrders={selectedOrders}
                        setSelectedOrders={setSelectedOrders}
                        watch={pickupWatch}
                      />
                    )}

                    {createType === "linehaul" && (
                      <LinehaulRequestModal
                        register={linehaulRegister}
                        errors={linehaulErrors}
                        setValue={linehaulSetValue}
                        watch={linehaulWatch}
                        step={linehaulStep}
                      />
                    )}

                    {createType === "return" && (
                      <ReturnToShipperModal
                        register={returnRegister}
                        errors={returnErrors}
                        setValue={returnSetValue}
                        watch={returnWatch}
                        control={returnControl}
                        clearErrors={returnClearErrors}
                      />
                    )}

                    {createType === "product" && (
                      <div className="relative group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 border border-[#D2D2D2]/70 p-2.5 transition-all duration-300 space-y-1.5">
                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                        <SectionHeader
                          icon={IconPackage}
                          title={t("Product Details")}
                        />
                        <div className="grid grid-cols-2 gap-4">
                          <NewSelectInput
                            className=""
                            label="Client"
                            name="toClient"
                            value={productWatch("toClient")}
                            register={productRegister}
                            error={productErrors?.toClient}
                            clearErrors={productClearErrors}
                            validation={{ required: "Please select Client" }}
                            forceDownward={true}
                            onChange={(val) => {
                              productSetValue("toClient", val);
                              productSetValue("products", []); // clear products selection on client change
                            }}
                            selectOptions={
                              clients?.map((client: any) => ({
                                id: client?.id,
                                value: client?.id,
                                text: client?.name,
                              })) || []
                            }
                          />

                          <MultiSelectInput
                            className=""
                            name="products"
                            label="Select Products"
                            disabled={!productWatch("toClient")}
                            placeholder={
                              productWatch("toClient")
                                ? "Select"
                                : "Select Client First"
                            }
                            value={productWatch("products") || []}
                            onChange={(val) => productSetValue("products", val)}
                            error={productErrors?.products as any}
                            selectOptions={
                              products?.map((product: any) => ({
                                id: product?.id,
                                value: product?.id,
                                text: `${product?.sku} (${product?.itemName})`,
                              })) || []
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {data?.totalPages > 1 && (
          <div className="mt-4 col-span-12">
            <Pagination
              totalPages={data.totalPages}
              currentPage={pageNo}
              fetchData={setPageNo}
              totalCount={data.total}
              itemLabel="manifests"
            />
          </div>
        )}
      </div>
    </>
  );
}
